import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { ReactNode } from "react";
import { breakpointMobileExtended } from "src/design-system/styles/breakpoints";
import { zIndex } from "src/design-system/styles/zIndices";
import { FullWidth, MainColumn } from "./BodyLayout";

export type StickyModes = "none" | "mobile" | "all";

type StickyProps = {
  sticky?: StickyModes;
  height: string;
  children: ReactNode;
  className?: string;
  hideGradient?: boolean;
  width?: string;
};

const Container = styled.div<{
  height: string;
  sticky?: StickyModes;
  hideGradient?: boolean;
}>`
  width: 100%;
  padding-bottom: 16px;
  height: ${({ height }) => height};

  @media ${mediaSelector} {
    position: fixed;
    bottom: 0;
    width: 100vw;
    z-index: ${zIndex.footer + 1};

    ${({ hideGradient }) =>
      hideGradient
        ? ""
        : css`
            background-image: linear-gradient(
              to bottom,
              rgba(253, 245, 244, 0.2),
              #fdf5f4
            );
          `};
  }
`;

const Placeholder = styled.div<{ height: string; sticky?: StickyModes }>`
  height: 0;

  @media ${mediaSelector} {
    height: ${({ height }) => height};
    margin: 0 auto 16px;
  }
`;

/**
 * Generates media selector to target mobile only or all viewports.
 */
function mediaSelector({ sticky }: { sticky?: StickyModes }) {
  if (sticky === "mobile") {
    return breakpointMobileExtended;
  }
  if (sticky === "all") {
    return "all";
  }
  // Disable the sticky behavior via media query.
  return "not all";
}

/**
 * Fixed height bar that is fixed to the bottom of the page, with
 * appropriate placeholder.
 */
export function StickyBar({
  sticky = "all",
  height,
  children,
  className,
  hideGradient,
  width,
}: StickyProps) {
  return (
    <>
      <Container
        height={height}
        sticky={sticky}
        className={className}
        hideGradient={hideGradient}
      >
        <FullWidth>
          <MainColumn maxWidth={width}>{children}</MainColumn>
        </FullWidth>
      </Container>
      {/* pad bottom of page when button is sticky. */}
      <Placeholder height={height} sticky={sticky} />
    </>
  );
}
